import easelAPI, {
  deleteOptions,
  EaselPayloadResponse,
  EaselResponse,
  getOptions,
  postOptions,
  putOptions,
} from 'apis/helpers/easelAPI'
import { objectToQueryString } from 'lib/helpers'

type EaselRequest = {
  hitsPerPage: number
  page: number
  search?: string
}

export type StudioArtwork = {
  artworkId: string
  artworkTitle: string
  artworkUrl: string
  category: string
  canBePublished: boolean
  visibility: 'draft' | 'published'
  originalStatus: 'avail' | 'unavail' | 'sold' | 'reserved'
  originalSku: string | null
  originalPrice: number | null
  // in cents
  originalAisp: number | null
  // in cents
  originalWeight: number | null
  // float
  originalHeight: number | null
  // float, in cm
  originalWidth: number | null
  // float, in cm
  originalDepth: number | null
  // float, in cm
  originalIso2Country: string | null
  // e.g. US
  originalPackagingOption: 'rolled' | 'flat_cardboard' | 'flat_crate' | null
  hasOpenEditions: boolean
  hasLimitedEditions: boolean
  artistArtworksOrder: number
  uploadedDate: number
  // timestamp
  imageUrl: string
  height: number
  // float, in cm
  depth: number
  // float, in cm
  width: number // float, in cm
}

type FetchMyStudioArtworksPayloadResponse = {
  total: number
  artworks: Array<StudioArtwork>
}

export const fetchArtworkMetadata = async (): Promise<EaselResponse> => {
  const options = getOptions
  const json = await easelAPI.fetchJSON({
    name: 'fetchArtworkMetadata',
    options,
    url: '/artwork/metadata',
  })
  return json.payload
}

export const fetchMyArtById = async (artworkId: number): Promise<EaselResponse> => {
  const options = getOptions
  const response = await easelAPI.fetchResponse({
    options,
    url: `/my-art/${artworkId}${window.location.search}`,
  })

  if (response.status === 404) {
    return {
      success: false,
      payload: {
        errorMessage: 'Sorry, artwork not found',
      },
    }
  }

  const json = await easelAPI.parseResponseToValidateLoginJSON(response, 'fetchMyArtById')
  return json
}

export const deleteMyArtById = async (artworkId: number): Promise<EaselResponse> => {
  const options = deleteOptions
  const json = await easelAPI.fetchValidateLoginJSON({
    name: 'deleteMyArtById',
    options,
    url: `/my-art/${artworkId}`,
  })
  return json
}

export const fetchMyStudioArtworks = async (
  easelRequest: EaselRequest
): Promise<EaselPayloadResponse<FetchMyStudioArtworksPayloadResponse>> => {
  const options = getOptions
  const json = await easelAPI.fetchValidateLoginJSON({
    name: 'fetchMyArt',
    options,
    url: `/my-art/studio?${objectToQueryString(easelRequest)}`,
  })
  return json
}

export const reorderMyArtworks = async (
  easelRequest: Array<{
    artworkId: string
    order: number
  }>
): Promise<{
  success: boolean
  statusCode: number
}> => {
  try {
    const options = { ...putOptions, body: JSON.stringify(easelRequest) }
    const response = await easelAPI.fetchResponse({
      options,
      url: `/my-art/studio/reorder`,
    })

    return {
      success: response.status === 200,
      statusCode: response.status,
    }
  } catch (error) {
    return {
      success: false,
      statusCode: 500,
    }
  }
}

export const fetchMyArt = async (query: Record<string, any>): Promise<EaselResponse> => {
  const options = getOptions
  const json = await easelAPI.fetchValidateLoginJSON({
    name: 'fetchMyArt',
    options,
    url: `/my-art?${objectToQueryString(query)}`,
  })
  return json
}

export const uploadMyArtwork = async (data: Record<string, any>): Promise<EaselResponse> => {
  const options = { ...postOptions, body: JSON.stringify(data) }
  const json = await easelAPI.fetchValidateLoginJSON({
    name: 'uploadMyArtwork',
    options,
    url: `/my-art/upload`,
  })
  return json
}

export const publishMyArtwork = async (artworkId: string): Promise<EaselResponse> => {
  const options = putOptions
  const json = await easelAPI.fetchValidateLoginJSON({
    name: 'publishMyArtwork',
    options,
    url: `/my-art/${artworkId}/publish`,
  })
  return json
}

export const duplicateMyArtwork = async (artworkId: string): Promise<EaselResponse> => {
  const options = postOptions
  const json = await easelAPI.fetchValidateLoginJSON({
    name: 'duplicateMyArtwork',
    options,
    url: `/my-art/${artworkId}/duplicate`,
  })
  return json
}

export const renewMyArtworkProducts = async (artworkId: string): Promise<EaselResponse> => {
  const options = postOptions
  const json = await easelAPI.fetchValidateLoginJSON({
    name: 'renewMyArtworkProducts',
    options,
    url: `/my-art/${artworkId}/renew-products`,
  })
  return json
}

export const updateMyArtwork = async (artwork: Record<string, any>): Promise<EaselResponse> => {
  const { artworkId } = artwork
  const options = { ...putOptions, body: JSON.stringify(artwork) }
  const response = await easelAPI.fetchResponse({
    options,
    url: `/my-art/${artworkId}/update`,
  })

  const json = await easelAPI.parseResponseToValidateLoginJSON(response, 'updateMyArtwork')

  if (response.status === 400) {
    if (json.message) {
      return {
        success: false,
        payload: {
          errorMessage: json.message,
        },
      }
    }
  }

  return json
}

export const updateMyArtworkVisibility = async ({
  artworkId,
  visibility,
}: Record<string, any>): Promise<EaselResponse> => {
  const options = putOptions
  const json = await easelAPI.fetchValidateLoginJSON({
    name: 'updateMyArtworkVisibility',
    options,
    url: `/my-art/${artworkId}/${visibility}`,
  })
  return json
}

export const uploadMyArtworkImage = async ({
  artworkImage,
  captchaAction,
  captchaToken,
}: Record<string, any>): Promise<EaselResponse> => {
  // Users can only upload types: jpgs, tif, etc... and the file size must be smaller than 50mb
  const megabyte = 1024000 // 1Mb => 1,024,000 bytes
  const megabyteLimit = 50 * megabyte // 50 mb
  if (artworkImage.size > megabyteLimit) {
    return {
      success: false,
      payload: {
        errorMessage: 'Your image exceeds the maximum upload size of 50MB.',
      },
    }
  }
  const response = await easelAPI.postFormData({
    url: '/my-art/image/upload',
    data: {
      image: artworkImage,
      captchaAction: captchaAction,
      captchaToken: captchaToken,
    },
  })

  if (response.status === 413) {
    return {
      success: false,
      payload: {
        errorMessage: 'Your image is too heavy',
      },
    }
  }

  const json = await easelAPI.parseResponseToValidateLoginJSON(response, 'uploadMyArtworkImage')

  if (response.status === 400) {
    if (json.message) {
      return {
        success: false,
        payload: {
          errorMessage: json.message,
        },
      }
    }
  }

  if (response.status !== 200) {
    return {
      success: false,
      payload: {},
    }
  }

  return json
}

export const bulkPublishMyArtwork = async (artworks: Array<string>): Promise<EaselResponse> => {
  const options = {
    ...postOptions,
    body: JSON.stringify({
      artworks,
    }),
  }
  const json = await easelAPI.fetchValidateLoginJSON({
    name: 'bulkPublishMyArtwork',
    options,
    url: `/my-art/bulk-actions/publish`,
  })
  return json
}

export const bulkDeleteMyArtwork = async (artworks: Array<string>): Promise<EaselResponse> => {
  const options = {
    ...postOptions,
    body: JSON.stringify({
      artworks,
    }),
  }
  const json = await easelAPI.fetchValidateLoginJSON({
    name: 'bulkDeleteMyArtwork',
    options,
    url: `/my-art/bulk-actions/delete`,
  })
  return json
}

export const bulkRenewMyArtworkProducts = async (
  artworks: Array<string>
): Promise<EaselResponse> => {
  const options = {
    ...postOptions,
    body: JSON.stringify({
      artworks,
    }),
  }
  const json = await easelAPI.fetchValidateLoginJSON({
    name: 'bulkDeleteMyArtwork',
    options,
    url: `/my-art/bulk-actions/renew-products`,
  })
  return json
}
